import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Send, Sparkles, MessageCircle, Heart, Calendar, Plus, Trash2,
  Download, BellRing, FileDown, ArrowRight, Star, X, Edit2,
} from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL, formatApiError } from "../../lib/api";

/* ============================================================
   AI WEDDING CONCIERGE
   ============================================================ */
export function ConciergePage() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    api.get("/concierge/history").then((r) => {
      const last = (r.data || []).slice(-50);
      setMessages(last.map((m) => ({ role: m.role, text: m.text })));
      if (last.length) setSessionId(last[last.length - 1].session_id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  async function send(e) {
    e.preventDefault();
    if (!input.trim() || busy) return;
    const text = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/concierge/chat", { message: text, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  }

  const SUGGESTIONS = [
    "What should I focus on this week?",
    "Suggest a photographer for under $5k in Atlanta",
    "Write a save-the-date message",
    "How do we handle a difficult RSVP?",
  ];

  return (
    <div className="space-y-6" data-testid="concierge-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">AI Wedding Concierge</p>
        <h1 className="font-serif font-light text-4xl md:text-5xl">Your personal planner — always on.</h1>
        <p className="text-[var(--muted)] mt-2">Ask anything. Vendor advice, timelines, etiquette, scripts. Powered by Claude Sonnet 4.5.</p>
      </div>

      <div className="bg-white border border-[var(--line)] rounded-3xl flex flex-col h-[640px] overflow-hidden">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-4">
          {messages.length === 0 && (
            <div className="text-center py-12">
              <Sparkles className="w-8 h-8 mx-auto text-[var(--gold)] mb-3" />
              <p className="font-serif text-2xl mb-2">How can I help?</p>
              <p className="text-sm text-[var(--muted)] mb-6">Try asking about timelines, budgets, or vendor selections.</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {SUGGESTIONS.map((s) => (
                  <button key={s} onClick={() => setInput(s)} data-testid="concierge-suggest" className="text-xs px-4 py-2 border border-[var(--line)] rounded-full hover:border-[var(--gold)] hover:text-[var(--gold)] transition">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap ${
                m.role === "user"
                  ? "bg-[var(--gold)] text-white"
                  : "bg-[var(--bg-warm)] text-[var(--ink)]"
              }`}>
                {m.text}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="bg-[var(--bg-warm)] rounded-2xl px-4 py-3 text-sm text-[var(--muted)]">
                <span className="inline-block animate-pulse">Concierge is typing…</span>
              </div>
            </div>
          )}
        </div>
        <form onSubmit={send} className="border-t border-[var(--line)] p-3 flex gap-2">
          <input
            data-testid="concierge-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask your concierge…"
            className="flex-1 bg-[var(--bg-warm)] rounded-full px-4 py-3 text-sm outline-none"
          />
          <button type="submit" disabled={busy} data-testid="concierge-send" className="w-12 h-12 rounded-full bg-[var(--gold)] flex items-center justify-center text-white disabled:opacity-50">
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}

/* ============================================================
   COMPATIBILITY QUIZ
   ============================================================ */
export function QuizPage() {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [partnerOne, setPartnerOne] = useState("");
  const [partnerTwo, setPartnerTwo] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/quiz/questions").then((r) => setQuestions(r.data || [])).catch(() => {});
  }, []);

  async function submit() {
    const orderedAnswers = questions.map((q) => answers[q.index] || 0);
    if (orderedAnswers.some((a) => !a)) {
      toast.error("Please answer all questions");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/quiz/submit", {
        answers: orderedAnswers,
        partner_one_name: partnerOne,
        partner_two_name: partnerTwo,
      });
      setResult(data);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  }

  if (result) {
    return (
      <div className="max-w-2xl mx-auto text-center space-y-6" data-testid="quiz-result">
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)]">Compatibility Result</p>
        <h1 className="font-serif font-light text-5xl">{result.percentage}%</h1>
        <p className="font-script text-4xl text-[var(--gold)]">{result.headline}</p>
        <p className="text-[var(--muted)]">Tier: <strong className="capitalize">{result.tier}</strong></p>

        <div className="text-left bg-white border border-[var(--line)] rounded-2xl p-6">
          <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)] mb-3">Your strongest dimensions</p>
          <ul className="space-y-2">
            {result.strongest.map((d) => (
              <li key={d} className="flex items-center gap-2 text-sm capitalize">
                <Heart className="w-4 h-4 text-[var(--gold)]" /> {d}
              </li>
            ))}
          </ul>
          {result.weakest.length > 0 && (
            <>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)] mt-5 mb-3">Areas worth a conversation</p>
              <ul className="space-y-2">
                {result.weakest.map((d) => (
                  <li key={d} className="flex items-center gap-2 text-sm capitalize">
                    <Sparkles className="w-4 h-4 text-[var(--muted)]" /> {d}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>

        <button onClick={() => { setResult(null); setAnswers({}); }} data-testid="quiz-retake" className="btn-outline-gold">
          Take it again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="quiz-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Compatibility Quiz</p>
        <h1 className="font-serif font-light text-4xl md:text-5xl">How locked in are you?</h1>
        <p className="text-[var(--muted)] mt-2">10 questions, 5 minutes. Honest answers only.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 max-w-xl">
        <input value={partnerOne} onChange={(e) => setPartnerOne(e.target.value)} placeholder="Your name" className="border border-[var(--line)] rounded-xl px-4 py-3 bg-white" data-testid="quiz-name1" />
        <input value={partnerTwo} onChange={(e) => setPartnerTwo(e.target.value)} placeholder="Partner's name" className="border border-[var(--line)] rounded-xl px-4 py-3 bg-white" data-testid="quiz-name2" />
      </div>

      <div className="space-y-5">
        {questions.map((q) => (
          <div key={q.index} className="bg-white border border-[var(--line)] rounded-2xl p-6">
            <p className="font-serif text-lg mb-4">{q.index + 1}. {q.text}</p>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3, 4, 5].map((v) => (
                <button
                  key={v}
                  onClick={() => setAnswers({ ...answers, [q.index]: v })}
                  data-testid={`quiz-q${q.index}-v${v}`}
                  className={`flex-1 min-w-[80px] px-3 py-2 rounded-xl text-sm border transition ${
                    answers[q.index] === v
                      ? "bg-[var(--gold)] text-white border-[var(--gold)]"
                      : "border-[var(--line)] hover:border-[var(--gold)]"
                  }`}
                >
                  {["Strongly disagree", "Disagree", "Neutral", "Agree", "Strongly agree"][v - 1]}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <button onClick={submit} disabled={busy} data-testid="quiz-submit" className="btn-gold">
        {busy ? "Calculating…" : "See our score"} <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

/* ============================================================
   LOVE STORY TIMELINE
   ============================================================ */
export function LoveStoryPage() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ date: "", title: "", description: "", event_type: "other", icon: "" });

  function load() { api.get("/love-story").then((r) => setEvents(r.data || [])).catch(() => {}); }
  useEffect(() => { load(); }, []);

  async function save(e) {
    e.preventDefault();
    if (!form.date || !form.title) return toast.error("Date and title required");
    try {
      if (editing) await api.patch(`/love-story/${editing}`, form);
      else await api.post("/love-story", form);
      toast.success(editing ? "Updated" : "Added");
      setOpen(false); setEditing(null); setForm({ date: "", title: "", description: "", event_type: "other", icon: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this milestone?")) return;
    await api.delete(`/love-story/${id}`); load();
  }

  function startEdit(ev) {
    setEditing(ev.event_id);
    setForm({
      date: ev.date, title: ev.title, description: ev.description || "",
      event_type: ev.event_type || "other", icon: ev.icon || "",
    });
    setOpen(true);
  }

  const TYPE_ICONS = {
    met: "💫", first_date: "🌹", moved_in: "🏠", engagement: "💍",
    wedding: "👰", honeymoon: "✈️", other: "❤️",
  };

  return (
    <div className="space-y-8" data-testid="love-story-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Love Story Timeline</p>
          <h1 className="font-serif font-light text-4xl md:text-5xl">Every chapter, beautifully kept.</h1>
          <p className="text-[var(--muted)] mt-2">From the moment you met to forever.</p>
        </div>
        <button onClick={() => { setOpen(true); setEditing(null); }} data-testid="ls-add" className="btn-gold">
          <Plus className="w-4 h-4" /> Add Milestone
        </button>
      </div>

      {events.length === 0 ? (
        <div className="text-center py-16 bg-[var(--bg-warm)] rounded-3xl">
          <Heart className="w-8 h-8 mx-auto text-[var(--gold)] mb-3" />
          <p className="font-serif text-2xl mb-2">Your story starts here</p>
          <p className="text-sm text-[var(--muted)] mb-6">Add the moment you met to begin.</p>
        </div>
      ) : (
        <div className="relative pl-10 border-l-2 border-[var(--gold-line)] space-y-8">
          {events.map((ev) => (
            <div key={ev.event_id} className="relative">
              <span className="absolute -left-[3.05rem] w-10 h-10 rounded-full bg-white border-2 border-[var(--gold)] flex items-center justify-center text-lg">
                {ev.icon || TYPE_ICONS[ev.event_type] || "❤️"}
              </span>
              <div className="bg-white border border-[var(--line)] rounded-2xl p-5">
                <div className="flex items-center justify-between gap-3 mb-2">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]">{ev.date}</p>
                  <div className="flex gap-2">
                    <button onClick={() => startEdit(ev)} className="text-[var(--muted)] hover:text-[var(--gold)]"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => remove(ev.event_id)} className="text-[var(--muted)] hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <p className="font-serif text-2xl">{ev.title}</p>
                {ev.description && <p className="text-sm text-[var(--muted)] mt-2 whitespace-pre-line">{ev.description}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6" onClick={() => setOpen(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 w-full max-w-md space-y-4">
            <div className="flex items-center justify-between">
              <p className="font-serif text-2xl">{editing ? "Edit milestone" : "New milestone"}</p>
              <button type="button" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full border border-[var(--line)] rounded-xl px-3 py-2" data-testid="ls-date" />
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title (e.g. Our first date)" className="w-full border border-[var(--line)] rounded-xl px-3 py-2" data-testid="ls-title" />
            <select value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} className="w-full border border-[var(--line)] rounded-xl px-3 py-2">
              {["met", "first_date", "moved_in", "engagement", "wedding", "honeymoon", "other"].map((t) => (
                <option key={t} value={t}>{t.replaceAll("_", " ")}</option>
              ))}
            </select>
            <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" className="w-full border border-[var(--line)] rounded-xl px-3 py-2" />
            <button type="submit" data-testid="ls-save" className="btn-gold w-full justify-center">Save</button>
          </form>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   ANNIVERSARY REMINDERS
   ============================================================ */
export function AnniversaryPage() {
  const [prefs, setPrefs] = useState({ enabled: true, notify_email: true, notify_sms: false, days_before: 7 });
  const [upcoming, setUpcoming] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/anniversary/prefs").then((r) => setPrefs(r.data)).catch(() => {});
    api.get("/anniversary/upcoming").then((r) => setUpcoming(r.data || [])).catch(() => {});
  }, []);

  async function save() {
    setBusy(true);
    try {
      await api.post("/anniversary/prefs", prefs);
      toast.success("Preferences saved");
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  }

  return (
    <div className="space-y-8" data-testid="anniversary-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Anniversary Reminders</p>
        <h1 className="font-serif font-light text-4xl md:text-5xl">Forever, kept current.</h1>
        <p className="text-[var(--muted)] mt-2">We'll remind you (and only you) before every milestone.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[var(--line)] rounded-2xl p-6 space-y-4">
          <p className="font-serif text-2xl">Notification preferences</p>
          <label className="flex items-center justify-between"><span>Enabled</span>
            <input type="checkbox" checked={prefs.enabled} onChange={(e) => setPrefs({ ...prefs, enabled: e.target.checked })} data-testid="anniv-enabled" />
          </label>
          <label className="flex items-center justify-between"><span>Email reminders</span>
            <input type="checkbox" checked={prefs.notify_email} onChange={(e) => setPrefs({ ...prefs, notify_email: e.target.checked })} />
          </label>
          <label className="flex items-center justify-between"><span>SMS reminders</span>
            <input type="checkbox" checked={prefs.notify_sms} onChange={(e) => setPrefs({ ...prefs, notify_sms: e.target.checked })} />
          </label>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-[var(--muted)]">Days before to remind</label>
            <input type="number" min={0} max={60} value={prefs.days_before} onChange={(e) => setPrefs({ ...prefs, days_before: parseInt(e.target.value || 0, 10) })} className="w-full mt-1 border border-[var(--line)] rounded-xl px-3 py-2" />
          </div>
          <button onClick={save} disabled={busy} data-testid="anniv-save" className="btn-gold w-full justify-center">
            <BellRing className="w-4 h-4" /> {busy ? "Saving…" : "Save preferences"}
          </button>
        </div>

        <div className="bg-[var(--bg-warm)] rounded-2xl p-6">
          <p className="font-serif text-2xl mb-4">Upcoming anniversaries</p>
          {upcoming.length === 0 && <p className="text-sm text-[var(--muted)]">Set your wedding date first to see anniversaries.</p>}
          <ul className="space-y-3">
            {upcoming.map((a) => (
              <li key={a.years} className="flex items-center gap-3 bg-white rounded-xl p-3">
                <div className="w-12 h-12 rounded-full border border-[var(--gold)] flex items-center justify-center font-serif text-xl text-[var(--gold)]">{a.years}</div>
                <div className="flex-1">
                  <p className="font-medium">{a.label}</p>
                  <p className="text-xs text-[var(--muted)]">{a.date} · {a.days_away} days away · {a.tier}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   PDF KEEPSAKE ALBUM
   ============================================================ */
export function AlbumPage() {
  const [busy, setBusy] = useState(false);

  async function download() {
    setBusy(true);
    try {
      const res = await api.get("/album/pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "lockedinforever-keepsake.pdf";
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Album downloaded");
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  }

  return (
    <div className="space-y-8" data-testid="album-page">
      <div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)] mb-2">Keepsake Album</p>
        <h1 className="font-serif font-light text-4xl md:text-5xl">Your wedding, on paper.</h1>
        <p className="text-[var(--muted)] mt-2">A printable PDF with your story, guests, timeline, and budget — beautifully formatted.</p>
      </div>

      <div className="bg-white border border-[var(--line)] rounded-3xl p-8 max-w-2xl">
        <FileDown className="w-10 h-10 text-[var(--gold)] mb-4" strokeWidth={1.4} />
        <p className="font-serif text-2xl mb-2">What's inside</p>
        <ul className="space-y-2 text-sm text-[var(--muted)] mb-6">
          <li>· Cover page with your names and date</li>
          <li>· Your love story and proposal</li>
          <li>· Full guest list with RSVPs and meal preferences</li>
          <li>· Wedding-party attire details</li>
          <li>· Complete budget breakdown</li>
          <li>· Planning timeline with completed tasks</li>
          <li>· Registry & funds</li>
        </ul>
        <button onClick={download} disabled={busy} data-testid="album-download" className="btn-gold">
          <Download className="w-4 h-4" /> {busy ? "Generating…" : "Download Album (PDF)"}
        </button>
      </div>
    </div>
  );
}
